import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateOutcomeIncident,
  useOutcomeIncidentList,
  useOutcomeIncidentTransitions,
  OUTCOME_INCIDENT_STATUS_LABELS,
  OUTCOME_INCIDENT_TYPE_LABELS,
  OUTCOME_INCIDENT_SEVERITY_LABELS,
  type OutcomeIncidentSeverity,
  type OutcomeIncidentStatus,
  type OutcomeIncidentSummaryDto,
  type OutcomeIncidentType,
} from '@/hooks/useFollowUp';
import {
  OUTCOME_INCIDENT_PERMISSIONS,
  canSeeOutcomeIncidentsMenu,
  hasPermission,
} from '@/lib/followUpPermissions';

const TYPE_OPTIONS: OutcomeIncidentType[] = [
  'no_felt_improvement',
  'indicator_unchanged',
  'dissatisfaction',
  'abnormal_reaction',
  'dispute_risk',
  'other',
];
const SEVERITY_OPTIONS: OutcomeIncidentSeverity[] = [
  'low',
  'normal',
  'high',
  'critical',
];
const STATUS_OPTIONS: OutcomeIncidentStatus[] = [
  'open',
  'investigating',
  'action_planned',
  'resolved',
  'closed_without_action',
];

export default function OutcomeIncidentsPage() {
  const { permissions } = useAuth();
  const { rows, total, page, limit, loading, error, fetchIncidents } =
    useOutcomeIncidentList();
  const [filterStatus, setFilterStatus] = useState<OutcomeIncidentStatus | ''>('');
  const [filterSeverity, setFilterSeverity] = useState<
    OutcomeIncidentSeverity | ''
  >('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState(1);

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const [actionTarget, setActionTarget] = useState<OutcomeIncidentSummaryDto | null>(
    null,
  );
  const [actionKey, setActionKey] = useState(0);

  const canRead = canSeeOutcomeIncidentsMenu(permissions);
  const canCreate = hasPermission(
    permissions,
    OUTCOME_INCIDENT_PERMISSIONS.CREATE,
  );
  const canUpdate = hasPermission(
    permissions,
    OUTCOME_INCIDENT_PERMISSIONS.UPDATE,
  );

  useEffect(() => {
    if (!canRead) return;
    void fetchIncidents({
      status: filterStatus || undefined,
      severity: filterSeverity || undefined,
      customerId: filterCustomer || undefined,
      search: search || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [
    filterStatus,
    filterSeverity,
    filterCustomer,
    search,
    pageInput,
    canRead,
    fetchIncidents,
  ]);

  if (!canRead) return <AccessDenied resource="疗效事件" />;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">疗效不达预期事件</h1>
          <p className="text-sm text-muted-foreground">
            无明显改善 / 指标未改善 / 客户不满 / 异常反应 / 纠纷风险事件统一登记。
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => {
              setCreateKey((k) => k + 1);
              setCreateOpen(true);
            }}
          >
            登记事件
          </Button>
        )}
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="filter-status">状态</Label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(
                  (e.target.value || '') as OutcomeIncidentStatus | '',
                )
              }
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">全部</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {OUTCOME_INCIDENT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-severity">严重度</Label>
            <select
              id="filter-severity"
              value={filterSeverity}
              onChange={(e) =>
                setFilterSeverity(
                  (e.target.value || '') as OutcomeIncidentSeverity | '',
                )
              }
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">全部</option>
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {OUTCOME_INCIDENT_SEVERITY_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-customer">客户 ID</Label>
            <Input
              id="filter-customer"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-search">标题搜索</Label>
            <Input
              id="filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th className="px-3 py-2">标题</th>
                <th className="px-3 py-2">客户</th>
                <th className="px-3 py-2">类型</th>
                <th className="px-3 py-2">严重度</th>
                <th className="px-3 py-2">状态</th>
                <th className="px-3 py-2">报告时间</th>
                <th className="px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    加载中…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    暂无事件
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2">{row.customerId}</td>
                  <td className="px-3 py-2">
                    {OUTCOME_INCIDENT_TYPE_LABELS[row.incidentType]}
                  </td>
                  <td className="px-3 py-2">
                    {OUTCOME_INCIDENT_SEVERITY_LABELS[row.severity]}
                  </td>
                  <td className="px-3 py-2">
                    {OUTCOME_INCIDENT_STATUS_LABELS[row.status]}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(row.reportedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    {canUpdate && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setActionKey((k) => k + 1);
                          setActionTarget(row);
                        }}
                      >
                        管理
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          共 {total} 条 · 第 {page} / {totalPages} 页
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput <= 1}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput >= totalPages}
            onClick={() => setPageInput((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      </div>

      {createOpen && (
        <Card className="space-y-3 border-blue-200 bg-blue-50 p-4">
          <h2 className="text-base font-semibold">登记疗效事件</h2>
          <CreateIncidentForm
            key={createKey}
            onCreated={() => {
              setCreateOpen(false);
              setPageInput(1);
              void fetchIncidents({ page: 1, limit: 20 });
            }}
            onCancel={() => setCreateOpen(false)}
          />
        </Card>
      )}

      {actionTarget && (
        <IncidentActionPanel
          key={actionKey}
          incident={actionTarget}
          onDone={() => {
            setActionTarget(null);
            void fetchIncidents({
              status: filterStatus || undefined,
              severity: filterSeverity || undefined,
              customerId: filterCustomer || undefined,
              search: search || undefined,
              page: pageInput,
              limit: 20,
            });
          }}
          onCancel={() => setActionTarget(null)}
        />
      )}
    </div>
  );
}

function CreateIncidentForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { createIncident, loading, error } = useCreateOutcomeIncident();
  const [customerId, setCustomerId] = useState('');
  const [incidentType, setIncidentType] = useState<OutcomeIncidentType>(
    'no_felt_improvement',
  );
  const [severity, setSeverity] = useState<OutcomeIncidentSeverity>('normal');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const submit = async () => {
    if (!customerId.trim() || !title.trim() || !description.trim()) return;
    const r = await createIncident({
      customerId: customerId.trim(),
      incidentType,
      severity,
      title: title.trim(),
      description: description.trim(),
    });
    if (r) onCreated();
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="i-cust">客户 ID</Label>
          <Input
            id="i-cust"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="i-title">标题</Label>
          <Input
            id="i-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="i-type">类型</Label>
          <select
            id="i-type"
            value={incidentType}
            onChange={(e) => setIncidentType(e.target.value as OutcomeIncidentType)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {OUTCOME_INCIDENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="i-sev">严重度</Label>
          <select
            id="i-sev"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as OutcomeIncidentSeverity)}
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {OUTCOME_INCIDENT_SEVERITY_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="i-desc">事件描述（必填）</Label>
        <textarea
          id="i-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          maxLength={2000}
          className="w-full rounded-md border bg-background p-2 text-sm"
        />
      </div>
      {error && <div className="text-sm text-red-700">{error.message}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          取消
        </Button>
        <Button onClick={submit} disabled={loading}>
          {loading ? '提交中…' : '登记'}
        </Button>
      </div>
    </div>
  );
}

function IncidentActionPanel({
  incident,
  onDone,
  onCancel,
}: {
  incident: OutcomeIncidentSummaryDto;
  onDone: () => void;
  onCancel: () => void;
}) {
  const transitions = useOutcomeIncidentTransitions();
  const { permissions } = useAuth();
  const canClose = hasPermission(
    permissions,
    OUTCOME_INCIDENT_PERMISSIONS.CLOSE,
  );
  const [actionPlan, setActionPlan] = useState('');
  const [conclusion, setConclusion] = useState('');
  const [closeDecision, setCloseDecision] = useState<
    'resolved' | 'closed_without_action'
  >('resolved');

  const investigate = async () => {
    const r = await transitions.investigate(incident.id, {
      expectedVersion: incident.version,
    });
    if (r) onDone();
  };
  const planAction = async () => {
    const r = await transitions.planAction(incident.id, {
      expectedVersion: incident.version,
      actionPlan: actionPlan.trim(),
    });
    if (r) onDone();
  };
  const closeIt = async () => {
    const r = await transitions.close(incident.id, {
      expectedVersion: incident.version,
      closeDecision,
      conclusion: conclusion.trim(),
    });
    if (r) onDone();
  };

  return (
    <Card className="space-y-3 border-amber-300 bg-amber-50 p-4">
      <h3 className="text-base font-semibold">
        {incident.title} · 状态 {OUTCOME_INCIDENT_STATUS_LABELS[incident.status]}
      </h3>
      {incident.status === 'open' && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={investigate} disabled={transitions.loading}>
            开始调查
          </Button>
        </div>
      )}
      {(incident.status === 'open' || incident.status === 'investigating') && (
        <div className="space-y-2">
          <Label htmlFor="i-action">行动方案（推进到 action_planned 时必填）</Label>
          <textarea
            id="i-action"
            value={actionPlan}
            onChange={(e) => setActionPlan(e.target.value)}
            rows={3}
            maxLength={2000}
            className="w-full rounded-md border bg-background p-2 text-sm"
          />
          <Button
            onClick={planAction}
            disabled={!actionPlan.trim() || transitions.loading}
          >
            制定行动
          </Button>
        </div>
      )}
      {canClose &&
        (incident.status === 'open' ||
          incident.status === 'investigating' ||
          incident.status === 'action_planned') && (
          <div className="space-y-2 rounded-md border border-red-300 bg-red-50 p-3">
            <Label htmlFor="i-decision">结案类型</Label>
            <select
              id="i-decision"
              value={closeDecision}
              onChange={(e) =>
                setCloseDecision(
                  e.target.value as 'resolved' | 'closed_without_action',
                )
              }
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="resolved">已解决</option>
              <option value="closed_without_action">关闭未处理</option>
            </select>
            <Label htmlFor="i-conclusion">结论（必填）</Label>
            <textarea
              id="i-conclusion"
              value={conclusion}
              onChange={(e) => setConclusion(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full rounded-md border bg-background p-2 text-sm"
            />
            <Button
              onClick={closeIt}
              disabled={!conclusion.trim() || transitions.loading}
            >
              结案
            </Button>
          </div>
        )}
      {(incident.status === 'resolved' ||
        incident.status === 'closed_without_action') && (
        <div className="text-sm text-muted-foreground">事件已是终态。</div>
      )}
      {transitions.error && (
        <div className="text-sm text-red-700">{transitions.error.message}</div>
      )}
      <div className="flex justify-end">
        <Button variant="ghost" onClick={onCancel}>
          收起
        </Button>
      </div>
    </Card>
  );
}
